import { isLogoKeyword } from '../logos';

// HELPERS FOR TEXT PROCESSING
const WORD_BOUNDARY = '[^a-zA-Z0-9À-ÿΑ-ωА-я]';
const SEPARATOR = "[\\-_\\s'''`]*";

const escapeRegex = (value: string) =>
  value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const buildKeywordRegex = (keyword: string, flags: string) => {
  const parts = keyword
    .toLowerCase()
    .trim()
    .split(/[-_\s'''`]+/)
    .filter(Boolean);

  if (parts.length === 0) return null;

  const pattern = parts.map(escapeRegex).join(SEPARATOR);

  return new RegExp(
    `(^|${WORD_BOUNDARY})(${pattern})(${WORD_BOUNDARY}|$)`,
    flags
  );
};

const keywordsOnly = (keywords: string[]) =>
  keywords.filter((keyword) => !isLogoKeyword(keyword));

/**
 * Highlights specified keywords in a sentence with bold tags.
 * Supports Unicode characters including Greek letters.
 *
 * @param sentence The sentence to be processed. Must be a string.
 * @param keywords An array of keywords to be highlighted. Must be an array of strings.
 * @returns The processed sentence with highlighted keywords enclosed in bold tags.
 */
export const highlightWords = (sentence: string, keywords: string[]): string =>
  keywordsOnly(keywords).reduce((result, keyword) => {
    const regex = buildKeywordRegex(keyword, 'gi');

    return regex
      ? result.replace(
          regex,
          (_, before, match, after) =>
            `${before}<b style="background-color: #22f550; border-radius: 2px; padding: 2px">${match}</b>${after}`
        )
      : result;
  }, sentence);
/**
 * Validate the sentence with the given regex expression.
 *
 * @param text string to validate.
 * @param regex regular expression for testing the string.
 * @param minLength number that sets the minimum length the string must be in order to pass validation.
 * @returns "true" if the regular expression test is successful and the string length is greater than or equal to the minimum length. If either of these tests fails, the function will return "false".
 */
export const validateString = (
  text: string | undefined,
  regex?: RegExp,
  minLength?: number
): boolean => {
  if (!text) return false;
  if (regex && !regex.test(text)) return false;
  if (minLength && !(text.length >= minLength)) return false;
  return true;
};

/**
 * Replaces all urls found in a given text with <a> elements.
 *
 * @param text text to be processed.
 * @returns The processed text with <a> elements instead of plain urls.
 */
export const replaceUrlsWithLinks = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(urlRegex, (url) => `<a href="${url}">${url}</a>`);
};

/**
 * Replaces all \n's found in a given text with <br> elements.
 *
 * @param text text to be processed.
 * @returns The processed text with <br> elements instead of \n's.
 */
export const replaceNewLinesWithBreaks = (text: string): string =>
  text.replaceAll('\n', '<br>');

/**
 * Returns true if string has one of the keywords.
 * Supports Unicode characters including Greek letters.
 *
 * @param string The text to be processed. Must be a string.
 * @param keywords List of keywords. Can include phrases separated by spaces.
 * @returns Boolean representing if string has one of the keywords.
 */
export const stringHasKeyword = (text: string, keywords: string[]): boolean =>
  keywordsOnly(keywords).some((keyword) => {
    const regex = buildKeywordRegex(keyword, 'i');
    return regex ? regex.test(text) : false;
  });

/**
 * Extracts the handle or ID from a social media profile URL.
 * If a plain username is provided, it is returned as-is (Edit Creator).
 *
 * @param url A full profile URL or plain username string.
 * @returns The last path segment of the URL (e.g. "@claude", "lofigirl", "UCXF6...").
 *
 */
export const normalizeTag = (value: string): string => {
  if (!value) return '';
  const input = value.trim();
  const href = /^https?:\/\//i.test(input) ? input : `https://${input}`;
  const url = new URL(href);
  const parts = url.pathname.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? input;
};
